/**
 * User Management Service
 * Calls the User Management Lambda API for user data
 * Follows the same singleton pattern as CostExplorerService
 */

import {
  UserListResponse,
  UserFiltersState,
  ManagedUserDetail,
  ExportOptions,
  AccountStatus,
} from '../types';

export class UserManagementService {
  private static instance: UserManagementService | null = null;
  private apiUrl: string | null = null;
  private adminToken: string | null = null;

  static getInstance(): UserManagementService {
    if (!UserManagementService.instance) {
      UserManagementService.instance = new UserManagementService();
    }
    return UserManagementService.instance;
  }

  constructor() {
    this.apiUrl = process.env.REACT_APP_USER_MANAGEMENT_API || null;
    this.adminToken = process.env.REACT_APP_ADMIN_API_TOKEN || null;
  }

  /**
   * Check if the User Management API is configured
   */
  isConfigured(): boolean {
    return !!this.apiUrl;
  }

  /**
   * List users with pagination and filters
   */
  async listUsers(
    filters: Partial<UserFiltersState>,
    page: number = 1,
    pageSize: number = 25
  ): Promise<UserListResponse> {
    const params = new URLSearchParams();
    params.set('page', page.toString());
    params.set('pageSize', pageSize.toString());

    if (filters.search) params.set('search', filters.search);
    if (filters.authMethod && filters.authMethod !== 'all') params.set('authMethod', filters.authMethod);
    if (filters.status && filters.status !== 'all') params.set('status', filters.status);
    if (filters.sortBy) params.set('sortBy', filters.sortBy);
    if (filters.sortOrder) params.set('sortOrder', filters.sortOrder);

    const response = await this.fetchWithRetry(`${this.apiUrl}?${params.toString()}`);

    if (!response.ok) {
      if (response.status === 401) throw new Error('UNAUTHORIZED');
      throw new Error(`Failed to fetch users: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Get detailed user information
   */
  async getUserDetail(userId: string): Promise<{ user: ManagedUserDetail }> {
    const response = await this.fetchWithRetry(`${this.apiUrl}/${userId}`);

    if (!response.ok) {
      if (response.status === 401) throw new Error('UNAUTHORIZED');
      if (response.status === 404) throw new Error('USER_NOT_FOUND');
      throw new Error(`Failed to fetch user detail: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Update a user's account status
   */
  async updateUserStatus(
    userId: string,
    status: AccountStatus,
    reason?: string
  ): Promise<{ success: boolean }> {
    const response = await this.fetchWithRetry(`${this.apiUrl}/${userId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, reason }),
    });

    if (!response.ok) {
      if (response.status === 401) throw new Error('UNAUTHORIZED');
      if (response.status === 404) throw new Error('USER_NOT_FOUND');
      if (response.status === 422) throw new Error('INVALID_STATUS');
      throw new Error(`Failed to update user status: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Export user data
   */
  async exportUsers(options: ExportOptions): Promise<Blob> {
    const response = await this.fetchWithRetry(`${this.apiUrl}/export`, {
      method: 'POST',
      body: JSON.stringify(options),
    });

    if (!response.ok) {
      if (response.status === 401) throw new Error('UNAUTHORIZED');
      if (response.status === 202) {
        // Async export - return the status message
        const data = await response.json();
        throw new Error(`ASYNC_EXPORT:${JSON.stringify(data)}`);
      }
      throw new Error(`Failed to export users: ${response.status}`);
    }

    return await response.blob();
  }

  /**
   * Fetch with single retry for 5xx errors
   */
  private async fetchWithRetry(url: string, options: RequestInit = {}): Promise<Response> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (this.adminToken) {
      headers['X-Admin-Token'] = this.adminToken;
    }

    const fetchOptions: RequestInit = {
      ...options,
      headers: { ...headers, ...(options.headers as Record<string, string> || {}) },
    };

    let response = await fetch(url, fetchOptions);

    // Retry once for 5xx errors
    if (response.status >= 500) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      response = await fetch(url, fetchOptions);
    }

    return response;
  }
}
